import { useEffect } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, AlertTitle } from '@mui/material';
import { captureException } from '@sentry/react';
import { Ban, MailX } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';
import { canUseProspectionMapV2 } from '@shared/utils/prospectionMapAccess';
import { isRATenantId } from '@shared/utils/tenant';

const PROSPECTION_LEAD_CONTACT_STATUS = gql(/* GraphQL */ `
  query ProspectionLeadContactStatus($leadId: uuid!) {
    prospection_map_contact_attempts(
      where: { lead_id: { _eq: $leadId }, declined_at: { _is_null: false } }
      order_by: { declined_at: desc }
      limit: 1
    ) {
      id
      declined_at
      user {
        first_name
        last_name
      }
    }
    prospection_map_emailable_leads(where: { lead_id: { _eq: $leadId } }) {
      lead_id
    }
  }
`);

type LeadDetailsProspectionContactCardProps = {
  leadId: string;
};

export const LeadDetailsProspectionContactCard = ({
  leadId,
}: LeadDetailsProspectionContactCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const inProspectionTenant =
    me?.tenant_id != null && isRATenantId(me.tenant_id);
  const { data, error } = useQuery(PROSPECTION_LEAD_CONTACT_STATUS, {
    variables: { leadId },
    skip: !inProspectionTenant,
  });
  useEffect(() => {
    if (error != null) {
      captureException(error, {
        tags: { feature: 'prospection-map-contact' },
      });
    }
  }, [error]);

  const declined = data?.prospection_map_contact_attempts[0];

  if (declined == null) {
    return null;
  }

  // Absent from the emailable view means "cannot be contacted from the map":
  // an opt-out *or* no usable email any more. The view cannot tell the two
  // apart (opt-outs are not exposed to agents), so the copy must not claim a
  // refusal. The emailable view is only readable with the new map, so for
  // anyone else an empty answer says nothing: they get the declined notice.
  const notContactable =
    me != null &&
    canUseProspectionMapV2(me) &&
    (data?.prospection_map_emailable_leads.length ?? 0) === 0;
  const agentName = [declined.user?.first_name, declined.user?.last_name]
    .filter(part => part != null && part !== '')
    .join(' ');

  return (
    <Alert
      severity={notContactable ? 'error' : 'warning'}
      icon={notContactable ? <Ban size={18} /> : <MailX size={18} />}
    >
      <AlertTitle>
        {notContactable
          ? t('Owner can no longer be contacted')
          : t('Prospection request declined')}
      </AlertTitle>
      {notContactable
        ? t(
            'This owner can no longer be contacted from the prospection map — they declined and either opted out of further requests or no longer have a usable email address.',
          )
        : agentName !== ''
        ? t('The owner declined the prospection request sent by {{name}}.', {
            name: agentName,
          })
        : t('The owner declined a prospection request from your team.')}
    </Alert>
  );
};
